<template>
    <div>
        <div class="page-content px-4 top">
            <template-breadcrumb v-if="isDataLoaded" :title="details.name" />
            <template-header v-if="isDataLoaded" :title="details.name" />
            <b-card footer-class="p-0" class="rounded-lg">
                <div class="profile-details">
                    <form>
                        <input type="hidden" name="id" :value="templateId" />
                    </form>

                    <loader-profile
                        v-if="!isDataLoaded"
                        :show-image="false"
                    ></loader-profile>

                    <b-row v-else-if="isDataLoaded">
                        <b-col lg="6">
                            <b-form-group
                                label-for="name"
                                label-cols="3"
                                label="Name:"
                            >
                                <b-input
                                    v-if="isEditMode"
                                    id="name"
                                    v-model="editDetails.name"
                                    :class="{
                                        'border-danger': errors.has('name')
                                    }"
                                    v-validate="'required'"
                                    name="name"
                                    type="text"
                                    placeholder="Name (required)"
                                    :disabled="isSavingEdit"
                                />
                                <div v-else class="col-form-label">
                                    {{ details.name }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="snippet"
                                label-cols="3"
                                label="Type:"
                            >
                                <div class="col-form-label">
                                    {{
                                        details.template_type
                                            ? details.template_type.value
                                            : ""
                                    }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="description"
                                label-cols="3"
                                label="Description:"
                            >
                                <b-textarea
                                    v-if="isEditMode"
                                    v-model="editDetails.description"
                                    :class="{
                                        'border-danger': errors.has(
                                            'description'
                                        )
                                    }"
                                    name="description"
                                    placeholder="Description (optional)"
                                    :disabled="isSavingEdit"
                                />
                                <div
                                    v-else
                                    class="col-form-label"
                                    v-html="
                                        convertNewLineToBreak(
                                            details.description
                                        )
                                    "
                                ></div>
                            </b-form-group>
                            <b-form-group
                                label-for="hashtag"
                                label-cols="3"
                                label="Hashtag:"
                            >
                                <b-input
                                    v-if="isEditMode"
                                    id="hashtag"
                                    v-model="editDetails.hashtag"
                                    v-validate="'no_whitespace'"
                                    :class="{
                                        'border-danger': errors.has('hashtag')
                                    }"
                                    name="hashtag"
                                    type="text"
                                    placeholder="Hashtag (optional)"
                                    :disabled="isSavingEdit"
                                />
                                <div v-else class="col-form-label">
                                    {{ details.hashtag }}
                                </div>
                            </b-form-group>
                        </b-col>
                        <b-col lg="6">
                            <b-form-group
                                label-for="last_used"
                                label-cols="3"
                                label="Last Used:"
                            >
                                <div class="col-form-label">
                                    {{ convertToDateFormat(details.last_used) }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="last_updated"
                                label-cols="3"
                                label="Last Updated:"
                            >
                                <div class="col-form-label">
                                    {{ _carbonDateDisplay(details.update_at) }}
                                </div>
                            </b-form-group>
                            <b-form-group
                                label-for="total_used"
                                label-cols="3"
                                label="Total Use:"
                            >
                                <div class="col-form-label">
                                    {{ details.total_used }}
                                </div>
                            </b-form-group>
                        </b-col>
                    </b-row>
                </div>
                <template v-slot:footer no-body>
                    <b-tabs card class="list" lazy v-if="templateId">
                        <b-tab>
                            <template v-slot:title>
                                <span>
                                    <i class="cus-page-white-text"></i>Content
                                </span>
                            </template>
                            <b-card-text>
                                <div>
                                    <button
                                        :disabled="isSaving"
                                        @click="save"
                                        class="btn btn-secondary mb-2"
                                    >
                                        <i class="cus-disk"></i>&nbsp; Save
                                    </button>
                                </div>
                                <textarea
                                    v-model="details.elements"
                                    name="content"
                                    ref="content"
                                    rows="10"
                                    class="form-control textarea-h-auto"
                                ></textarea>
                            </b-card-text>
                        </b-tab>
                    </b-tabs>
                </template>
            </b-card>
        </div>
    </div>
</template>

<script>
import { DateTime } from "luxon";
import TEMPLATES from "@/constants/templates";
import TEMPLATE_SERVICES from "@/services/templates";
import SettingTemplateMixin from "@/mixins/settings-template";

export default {
    mixins: [SettingTemplateMixin],
    components: {
        TemplateBreadcrumb: () =>
            import("@/module/settings/template/breadcrumb.vue"),
        TemplateHeader: () => import("@/module/settings/template/header.vue"),
        LoaderProfile: () => import("@/components/elements/loader-profile.vue")
    },
    data() {
        return {
            TEMPLATES,
            templateId: null,
            isDataLoaded: false,
            isSaving: false
        };
    },
    computed: {
        details: {
            get() {
                return this.$store.state.settingsTemplate.details;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setDetails", value);
            }
        },
        editDetails: {
            get() {
                return this.$store.state.settingsTemplate.editDetails;
            },
            set(value) {
                this.$store.commit("settingsTemplate/setEditDetails", value);
            }
        },
        isEditMode: {
            get() {
                return this.$store.state.settingsTemplate.isEditMode;
            }
        },
        isSavingEdit() {
            return this.$store.state.settingsTemplate.isSavingEdit;
        },
        routeParam() {
            return this.$route.params.id;
        }
    },
    watch: {
        routeParam() {
            this.fetchData();
        }
    },
    async mounted() {
        await this.fetchData();

        this.$store.commit(
            "settingsTemplate/setTemplateType",
            TEMPLATES.TYPE.CONTENT_SNIPPET
        );

        this.$nextTick(() => {
            this.$refs.content.scrollIntoView();
            this.$refs.content.focus();
        });
    },
    beforeDestroy() {
        // clear vuex store
        this.$store.dispatch("settingsTemplate/reset");
    },
    methods: {
        errorClass(key) {
            return {
                "border-danger": this.errors.has(key)
            };
        },
        async fetchData(updatedData = null) {
            try {
                this.isDataLoaded = false;
                let encodedTemplateId = this.$route.params.id;
                this.templateId = null;

                let fetchDetails = updatedData;
                if (!updatedData) {
                    fetchDetails = await this.$http.get(
                        `${process.env.BARRAMUNDI}account/template/${encodedTemplateId}?encoded=true`
                    );
                }

                let res = fetchDetails.data.response;
                this.details = res.resources || res.resource;
                this.templateId = this.details.id;

                this.isDataLoaded = true;

                this.validateTemplateType(
                    this.details.type,
                    TEMPLATES.TYPE.CONTENT_SNIPPET
                );

                // store templateId in vuex
                this.$store.commit("settingsTemplate/setTemplateId", this.templateId); // prettier-ignore
            } catch (error) {
                console.log(error);
                this.isDataLoaded = true;
                this._message(error.message, "danger");
            }
        },
        async save() {
            let res = {};

            try {
                this.isSaving = true;

                res = await TEMPLATE_SERVICES.putUpdateSnippet(
                    this.templateId,
                    {
                        id: this.templateId,
                        type: TEMPLATES.TYPE.CONTENT_SNIPPET,
                        elements: this.details.elements
                    }
                );

                if (res.data.response && res.data.response.state != 200) {
                    throw new Error(res.data.response.message);
                }

                this._message(res.data.response.message);
            } catch (error) {
                console.log(error.message);
                this._message("Error saving data", "danger");
            } finally {
                this.isSaving = false;
                this.fetchData(res);
            }
        },
        async saveDetails() {
            let self = this;

            this.$store.commit("settingsTemplate/toggleSavingEdit", true);

            try {
                let isValid = await this.$validator.validateAll();

                if (!isValid) {
                    this.errors
                        .all()
                        .forEach(error => self._message(error, "danger"));
                    this.$store.commit(
                        "settingsTemplate/toggleSavingEdit",
                        false
                    );
                    return;
                }

                let res = await TEMPLATE_SERVICES.putUpdateSnippet(
                    this.templateId,
                    {
                        id: this.templateId,
                        type: this.editDetails.template_type.id,
                        name: this.editDetails.name,
                        description: this.editDetails.description,
                        hashtag: this.editDetails.hashtag || null
                    }
                );

                this.$store.commit("settingsTemplate/commitEditDetails");

                this.fetchData(res);

                this._message("Record saved");
            } catch (error) {
                console.log(error);
                this._message("Error saving data", "danger");
            }

            this.$store.commit("settingsTemplate/toggleSavingEdit", false);
        },
        convertToDateFormat(epochTimestamp) {
            if (!epochTimestamp) {
                return "";
            }

            // Convert epoch timestamp in seconds to milliseconds for Luxon
            const dateTime = DateTime.fromMillis(epochTimestamp * 1000);

            // Format the date as desired, e.g., "yyyy-MM-dd"
            return dateTime.toFormat(this._dateFormat);
        }
    }
};
</script>

<style scoped>
.top {
    margin-top: 10px;
    margin-bottom: 3px;
}
#app .textarea-h-auto {
    height: auto;
}
</style>
